import { useT } from '@/i18n/runtime';
import { type Platform, detectBrowser, detectPlatform, iosBrowserApp, iosLocationEntry, locationRecoveryFor, recoveryFor } from '@/media/permissions';
import { Icon } from '../components/Icon';

/**
 * The illustrated, OS-specific recovery card (SPEC_CAPTURE_UX_R2 §7.4, P1/P10): what to tap when the camera or the
 * location is blocked. Android Chrome = the lock icon in the address bar; iOS Safari = the Settings path in words (iOS
 * permits no deep link). The illustration is inline line-art, no asset, no text baked in; the steps are the deck's.
 */
export function RecoveryCard({ kind, ua, osLevel = false }: { kind: 'camera' | 'location'; ua?: string; osLevel?: boolean }): JSX.Element {
  const { t } = useT();
  const agent = ua ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  const platform: Platform = detectPlatform(agent);
  const steps = kind === 'location' ? locationRecoveryFor(platform) : platform === 'ios' && !osLevel ? ['sv.perm.cam.ios.1', 'sv.perm.cam.ios.2', 'sv.perm.cam.ios.3'] : recoveryFor(detectBrowser(agent), osLevel);
  // F-21: an iPhone step names the browser in hand (Settings → Chrome → Camera; Location Services → Chrome) — Safari's
  // names are not true for Chrome or Firefox on iOS
  const app = (kind === 'location' ? iosLocationEntry(agent) : iosBrowserApp(agent)) ?? t('sv.perm.ios.app.other');
  return (
    <div className="sv-recovery" data-testid={`recovery-${kind}`} data-platform={platform}>
      <div className="sv-recovery-head">
        <span className="sv-recovery-ill" aria-hidden="true">
          <RecoveryIllustration platform={platform} />
        </span>
        <div>
          <div className="pt">{kind === 'location' ? t('sv.perm.loc.title') : t('sv.perm.cam.title')}</div>
          <div className="pd">{t(`sv.perm.platform.${platform}`)}</div>
        </div>
      </div>
      <ol className="perm-steps sv-recovery-steps">
        {steps.map((k) => (
          <li key={k}>{t(k, { app })}</li>
        ))}
      </ol>
      <div className="sv-recovery-foot">
        <Icon name="info" size={12} /> {t('sv.perm.rec.then')}
      </div>
    </div>
  );
}

/** Line-art: Android — a phone with an address bar and a lock; iOS — a gear and a compass (Settings → Safari); other — a phone. */
function RecoveryIllustration({ platform }: { platform: Platform }): JSX.Element {
  if (platform === 'ios') {
    return (
      <svg viewBox="0 0 64 64" width="56" height="56" fill="none" aria-hidden="true">
        <title>settings</title>
        <circle cx="22" cy="32" r="11" stroke="currentColor" strokeWidth="2" />
        <circle cx="22" cy="32" r="4" stroke="currentColor" strokeWidth="2" />
        <path d="M22 17v-5M22 52v-5M7 32H2M42 32h-5M11.4 21.4l-3.5-3.5M36.1 46.1l-3.5-3.5M11.4 42.6l-3.5 3.5M36.1 17.9l-3.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M44 32h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="55" cy="44" r="7" stroke="currentColor" strokeWidth="2" />
        <path d="M58 41l-2 5-4 1 2-5z" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 64 64" width="56" height="56" fill="none" aria-hidden="true">
      <title>address bar</title>
      <rect x="14" y="4" width="36" height="56" rx="6" stroke="currentColor" strokeWidth="2" />
      <rect x="19" y="12" width="26" height="8" rx="4" stroke="currentColor" strokeWidth="2" />
      <rect x="22" y="14.5" width="4" height="3.5" rx="0.8" fill="currentColor" />
      <path d="M22.7 14.5v-1a1.3 1.3 0 012.6 0v1" stroke="currentColor" strokeWidth="1.2" />
      <path d="M30 16h11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <rect x="19" y="26" width="26" height="26" rx="3" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2" />
      {platform === 'android' ? <circle cx="24" cy="16.2" r="5.5" stroke="currentColor" strokeWidth="1.5" /> : null}
    </svg>
  );
}
